import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import type React from "react";
import { useMemo, useState } from "react";

import { METRIC_DEFINITIONS } from "../helpers/outreachSources";
import { Spacing } from "../types/enum";

/**
 * What every number on the results board means.
 *
 * The board is full of words that look ordinary and aren't — "reached" counts
 * people rather than messages, "accepted" excludes members who found the event
 * themselves, "too early to say" is a deliberate third state rather than a gap
 * in the data. Someone reading a percentage needs to know that before trusting
 * it.
 *
 * Asking about one column answers that one column: the question mark opens a
 * small popover beside itself with that definition and nothing else. Opening the
 * whole list to answer "what is Reached" makes the reader do the finding, which
 * is the work they pressed the button to avoid.
 *
 * The full list is still one click further on, from the link in the popover or
 * from the top of the page, because the terms do explain each other — "came to
 * that event" against "came to a conversation" only really lands side by side.
 */

const BY_TERM = new Map(METRIC_DEFINITIONS.map((entry) => [entry.term, entry]));

/**
 * A question mark that explains one term, beside the thing it explains.
 *
 * Falls back to the whole list if the term isn't one we've written up — better
 * an answer that takes a moment to find than a button that does nothing.
 */
const DefinitionsButton: React.FC<{ term?: string; onOpenAll: () => void }> = ({
  term,
  onOpenAll,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const definition = term ? BY_TERM.get(term) : undefined;

  return (
    <>
      <Tooltip title={definition ? `What does "${term}" mean?` : "What do these numbers mean?"}>
        <IconButton
          size="small"
          aria-label={definition ? `What does ${term} mean?` : "What do these numbers mean?"}
          onClick={(event) => {
            // The board's rows are clickable; asking for help shouldn't also
            // open a tool underneath the popover.
            event.stopPropagation();
            if (definition) {
              setAnchor(event.currentTarget);
            } else {
              onOpenAll();
            }
          }}
          sx={{ p: 0.25, ml: 0.25, color: "text.secondary" }}
        >
          <HelpOutlineIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        // The column headers are right-aligned and near the edge, so let it sit
        // wherever it fits rather than running off the table.
        slotProps={{ paper: { sx: { maxWidth: 340, p: Spacing.m } } }}
      >
        {definition && (
          <>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {definition.term}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {definition.detail}
            </Typography>
            <Box mt={Spacing.s}>
              <Link
                component="button"
                type="button"
                variant="caption"
                underline="always"
                onClick={() => {
                  setAnchor(null);
                  onOpenAll();
                }}
              >
                See all definitions
              </Link>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

const DefinitionsDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
    <DialogTitle>What these numbers mean</DialogTitle>
    <DialogContent dividers>
      <Box display="flex" flexDirection="column" gap={Spacing.m}>
        {METRIC_DEFINITIONS.map((definition) => (
          <Box key={definition.term}>
            <Typography variant="subtitle2" fontWeight={700}>
              {definition.term}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {definition.detail}
            </Typography>
          </Box>
        ))}
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

/**
 * Holds the full-list dialog and hands back a question mark factory, so a screen
 * can put one beside any column without threading state through every one. Each
 * button owns its own popover; only the shared dialog needs to live up here.
 */
export const useDefinitions = (): {
  open: () => void;
  helpButton: (term?: string) => React.ReactNode;
  dialog: React.ReactNode;
} => {
  const [isOpen, setIsOpen] = useState(false);

  return useMemo(
    () => ({
      open: () => setIsOpen(true),
      helpButton: (term?: string) => (
        <DefinitionsButton key={term} term={term} onOpenAll={() => setIsOpen(true)} />
      ),
      dialog: <DefinitionsDialog open={isOpen} onClose={() => setIsOpen(false)} />,
    }),
    [isOpen],
  );
};
